import React, { useEffect, useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ModalProps } from '../../types';
import Button from './button';
import BriqqLogo from '../brand/briqq-logo';
import { authService, SignInData, SignUpData, getFirebaseErrorMessage } from '../../services/auth';
import styles from './modal.module.css';

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, showLoginForm, onToggleForm, onAuthSuccess, errorMessage }) => {
  const [submitMessage, setSubmitMessage] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const navigate = useNavigate();
  // For development testing: change this to true to enable signup
  const [isSignUp] = useState(false);

  const titleId = useId();

  // Clear submit message when modal opens
  useEffect(() => {
    if (isOpen) {
      setSubmitMessage('');
    }
  }, [isOpen]);

  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const formData = new FormData(e.target as HTMLFormElement);
    const email = formData.get('email') as string;

    // Immediately show success message to user
    setSubmitMessage("Successfully joined the waitlist! We'll notify you when Briqq is ready.");
    
    // Clear the form immediately
    const form = e.target as HTMLFormElement;
    form.reset();

    // Handle backend silently (user doesn't need to wait)
    const apiUrl = process.env.NODE_ENV === 'production' 
      ? 'https://us-central1-briqq-b92a6.cloudfunctions.net/waitlist'
      : 'http://localhost:5001/briqq-b92a6/us-central1/waitlist';

    // Intentionally not awaited; log if it fails but don't interrupt UX
    void fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    }).catch(error => {
      console.error('Error submitting waitlist:', error);
    });
  };

  const handleAuthSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');

    const formData = new FormData(e.target as HTMLFormElement);
    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;

    try {
      if (isSignUp) {
        const signUpData: SignUpData = {
          name,
          email,
          password,
        };
        await authService.signUp(signUpData);
      } else {
        const signInData: SignInData = {
          email,
          password,
        };
        await authService.signIn(signInData);
      }
      
      onClose();
      // Navigate to dashboard after successful authentication
      navigate('/dashboard');
      // Call the auth success callback if provided
      if (onAuthSuccess) {
        onAuthSuccess();
      }
    } catch (err) {
      setLoginError(getFirebaseErrorMessage(err));
    } finally {
      setLoginLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" aria-label="Close modal" className={styles.modalClose} onClick={onClose}>×</button>
        
        <div className={styles.modalBrand}>
          <BriqqLogo size="medium" />
        </div>
        
        <h2 id={titleId} className={styles.modalTitle}>
          {showLoginForm ? 'Sign in to Briqq' : 'Join the waitlist'}
        </h2>
        <p className={styles.modalSubtitle}>
          {showLoginForm ? 'Welcome back! Sign in to access your account.' : 'Enter your email address and we\'ll let you know when your spot is ready'}
        </p>
        
        {errorMessage && (
          <div className={styles.errorMessage}>
            {errorMessage}
          </div>
        )}
        
        {!showLoginForm ? (
          <form onSubmit={handleWaitlistSubmit} className={styles.modalForm}>
            <div className={styles.modalField}>
              <label htmlFor="waitlistEmail" className={styles.modalLabel}>Email address</label>
              <input
                type="email"
                id="waitlistEmail"
                name="email"
                placeholder="Enter your email address"
                required
                className={styles.modalInput}
              />
            </div>
            
            <Button 
              type="submit" 
              className={styles.modalSubmit}
            >
              Join the waitlist
            </Button>
            
            {submitMessage && (
              <div className={styles.submitMessage}>
                {submitMessage}
              </div>
            )}
            
            <div className={styles.modalSwitch}>
              Already have access? <button type="button" className={styles.modalSwitchLink} onClick={onToggleForm}>Sign in</button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleAuthSubmit} className={styles.modalForm}>
            {loginError && (
              <div className={styles.errorMessage}>
                {loginError}
              </div>
            )}
            
            {/* Signup form hidden from users - only available for development */}
            {isSignUp && (
              <div className={styles.modalField}>
                <label htmlFor="authName" className={styles.modalLabel}>Full name</label>
                <input
                  type="text"
                  id="authName"
                  name="name"
                  placeholder="Enter your full name"
                  required
                  className={styles.modalInput}
                  disabled={loginLoading}
                />
              </div>
            )}
            
            <div className={styles.modalField}>
              <label htmlFor="authEmail" className={styles.modalLabel}>Email address</label>
              <input
                type="email"
                id="authEmail"
                name="email"
                placeholder="Enter your email address"
                required
                className={styles.modalInput}
                disabled={loginLoading}
              />
            </div>
            
            <div className={styles.modalField}>
              <label htmlFor="authPassword" className={styles.modalLabel}>Password</label>
              <input
                type="password"
                id="authPassword"
                name="password"
                placeholder="Enter your password"
                required
                className={styles.modalInput}
                disabled={loginLoading}
              />
            </div>
            
            <Button 
              type="submit" 
              className={styles.modalSubmit}
              disabled={loginLoading}
            >
              {loginLoading ? 'Signing in...' : 'Sign in'}
            </Button>
            
            <div className={styles.modalSwitch}>
              {/* Signup option hidden from users - only available for development */}
              {/* To enable signup for testing, uncomment the line below: */}
              {/* <>Don't have an account? <button className={styles.modalSwitchLink} onClick={() => setIsSignUp(true)}>Sign up</button></> */}
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Modal; 